import { get, put } from "utils/apiClient"
import React, { createContext, useState } from "react"
import Cookies from "js-cookie"

const TenantConnectionContext = createContext()

const isAdmin = () => Cookies.get("role") === "admin"

function Provider({ children }) {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [tenantData, setTenantData] = useState(null)
  const [updateResponse, setUpdateResponse] = useState()
  const [integrationData, setIntegrationData] = useState(null)
  const [integrationUpdateResponse, setIntegrationUpdateResponse] = useState()
  // Admins target a specific tenant by SCAC; everyone else operates on their
  // own tenant (resolved server-side from the JWT), so scac stays ''.
  const [scac, setScac] = useState("")

  const scacQuery = (extra = "") => {
    const parts = []
    if (scac) parts.push(`scac=${encodeURIComponent(scac)}`)
    if (extra) parts.push(extra)
    return parts.length ? `?${parts.join("&")}` : ""
  }

  // Fetch the full real value of a single masked secret on demand (admin-gated
  // server-side). Used by the reveal (👁) toggle. Returns the value or null.
  const revealSecret = async fieldPath => {
    const data = await get(
      `crs/connections${scacQuery(`reveal=${encodeURIComponent(fieldPath)}`)}`
    )
    return data ? data.value : null
  }

  const loadTenant = async () => {
    setLoading(true)
    setTenantData(null) // clear stale data so a tenant switch doesn't flash the previous one
    try {
      const data = await get(`crs/connections${scacQuery()}`)
      if (data) setTenantData(data)
      setLoading(false)
    } catch (err) {
      console.error("Load error:", err)
      setLoading(false)
      setErrorMessage(err.message)
      setError(true)
    }
  }

  const submitUpdate = async payload => {
    setLoading(true)
    try {
      const savedData = await put(`crs/connections${scacQuery()}`, payload)
      await loadTenant()
      setUpdateResponse({
        message: "Connection updated successfully",
        data: savedData,
      })
      setLoading(false)
      setSuccess(true)
    } catch (err) {
      console.error("Update error:", err)
      setLoading(false)
      setErrorMessage(err.message)
      setError(true)
    }
  }

  const loadIntegration = async () => {
    setLoading(true)
    setIntegrationData(null) // clear stale data before loading the new tenant
    try {
      const data = await get(`crs/connections${scacQuery("type=integration")}`)
      if (data) setIntegrationData(data)
      setLoading(false)
    } catch (err) {
      console.error("Load integration error:", err)
      setLoading(false)
      setErrorMessage(err.message)
      setError(true)
    }
  }

  const submitIntegrationUpdate = async payload => {
    setLoading(true)
    try {
      const savedData = await put(
        `crs/connections${scacQuery("type=integration")}`,
        payload
      )
      await loadIntegration()
      setIntegrationUpdateResponse({
        message: "Integration updated successfully",
        data: savedData,
      })
      setLoading(false)
      setSuccess(true)
    } catch (err) {
      console.error("Update integration error:", err)
      setLoading(false)
      setErrorMessage(err.message)
      setError(true)
    }
  }

  const valueToShare = {
    loading,
    setLoading,
    success,
    setSuccess,
    error,
    setError,
    errorMessage,
    tenantData,
    setTenantData,
    integrationData,
    setIntegrationData,
    loadTenant,
    submitUpdate,
    revealSecret,
    loadIntegration,
    submitIntegrationUpdate,
    updateResponse,
    integrationUpdateResponse,
    scac,
    setScac,
    isAdmin: isAdmin(),
  }
  return (
    <TenantConnectionContext.Provider value={valueToShare}>
      {children}
    </TenantConnectionContext.Provider>
  )
}

export { Provider }
export default TenantConnectionContext
